<template>
  <section class="page" data-testid="page-debug">
    <header class="page-header">
      <div><h1>调试</h1><p>模拟消息管线，查看回复、trace 和 memory debug。</p></div>
      <div class="actions">
        <button class="button" :disabled="running" @click="resetInput">清空输入</button>
        <button class="button" :disabled="!payload" @click="copyResult">复制结果</button>
      </div>
    </header>
    <div class="split">
      <div class="panel">
        <h2>模拟输入</h2>
        <div class="toolbar" v-if="presets.length">
          <input v-model="presetQuery" placeholder="搜索预设" />
          <button class="button" :class="{ primary: presetFilter === 'all' }" @click="presetFilter = 'all'">全部</button>
          <button class="button" :class="{ primary: presetFilter === 'group' }" @click="presetFilter = 'group'">群聊</button>
          <button class="button" :class="{ primary: presetFilter === 'private' }" @click="presetFilter = 'private'">私聊</button>
          <button class="button" :class="{ primary: presetFilter === 'affinity' }" @click="presetFilter = 'affinity'">关系</button>
        </div>
        <div v-if="presets.length" class="preset-grid">
          <button
            v-for="preset in filteredPresets"
            :key="preset.id"
            type="button"
            class="preset-button"
            :class="{ active: selectedPreset === preset.id }"
            @click="applyPreset(preset)"
          >
            <strong>{{ preset.title }}</strong>
            <span>{{ preset.description }}</span>
          </button>
        </div>
        <div v-if="presets.length && !filteredPresets.length" class="empty">没有匹配的预设。</div>
        <div v-if="selectedExpectationItems.length" class="expectation-catalog">
          <strong>本预设检查</strong>
          <span v-for="item in selectedExpectationItems" :key="item.path" :title="item.path">
            {{ item.label }}
          </span>
        </div>
        <FormField label="消息"><textarea v-model="form.message" data-testid="debug-message-input" /></FormField>
        <FormField label="图片 URL"><textarea v-model="imageUrlsText" placeholder="https://example.com/image.png" /></FormField>
        <FormField label="语音 URL"><input v-model="recordUrl" placeholder="https://example.com/voice.wav" /></FormField>
        <div class="form-grid">
          <FormField label="用户"><input v-model="form.user_id" /></FormField>
          <FormField label="群"><input v-model="form.group_id" /></FormField>
          <FormField label="人设">
            <select v-model="selectedPersona" @change="applySelectedPersona">
              <option value="">默认人设</option>
              <option v-for="name in personas" :key="name" :value="name">{{ name }}</option>
              <option value="__custom">自定义</option>
            </select>
          </FormField>
          <FormField v-if="selectedPersona === '__custom'" label="自定义人设"><input v-model="form.persona" /></FormField>
          <FormField label="昵称"><input v-model="form.nickname" /></FormField>
          <FormField label="Bot QQ"><input v-model="form.self_id" /></FormField>
          <FormField label="模拟 @Bot"><input type="checkbox" v-model="form.at_bot" /></FormField>
          <FormField label="模拟戳一戳"><input type="checkbox" v-model="form.notice_poke" /></FormField>
          <FormField label="关系等级">
            <select v-model="form.affinity_level">
              <option value="">真实状态</option>
              <option value="hostile">敌对</option>
              <option value="cold">冷淡</option>
              <option value="neutral">普通</option>
              <option value="friendly">友好</option>
              <option value="close">亲近</option>
              <option value="trusted">信任</option>
            </select>
          </FormField>
        </div>
        <FormField label="历史 JSON"><textarea v-model="historyJson" /></FormField>
        <FormField label="群互动 JSON"><textarea v-model="interactionHistoryJson" placeholder='[{"user_id":"10007","nickname":"阿北","content":"先看配置"}]' /></FormField>
        <p v-if="error" class="error-text">{{ error }}</p>
        <p v-if="actionResult" class="save-result">{{ actionResult }}</p>
        <button class="button primary" :disabled="running || !hasRunnableInput" data-testid="debug-simulate-submit" @click="simulate">{{ running ? "模拟中..." : "模拟" }}</button>
      </div>
      <div class="panel">
        <h2>结果</h2>
        <div v-if="payload">
          <div class="metrics-grid">
            <MetricCard label="Dry-run" :value="payload.dry_run ? '真实管线' : '否'" />
            <MetricCard label="停止" :value="payload.decision?.stopped ? '是' : '否'" />
            <MetricCard label="停止原因" :value="payload.decision?.stop_reason || '-'" />
            <MetricCard label="人设" :value="payload.decision?.persona || '-'" />
            <MetricCard label="投递" :value="replyDelivery?.delivery_type || payload.decision?.delivery_type || '-'" />
            <MetricCard label="复读" :value="replyNoveltySummary" />
            <MetricCard label="表情决策" :value="stickerDecisionSummary" />
            <MetricCard label="禁区" :value="tabooSummary" />
            <MetricCard label="关系" :value="relationshipSummary" />
            <MetricCard label="画像" :value="userProfileSummary" />
            <MetricCard label="Prompt" :value="promptContextSummary" />
            <MetricCard label="情绪" :value="emotionSummary" />
            <MetricCard label="RAG 召回" :value="memoryRecallSummary" />
            <MetricCard label="发送等待" :value="deliveryTimingSummary" />
            <MetricCard label="多模态" :value="multimodalSummary" />
            <MetricCard label="被 @" :value="payload.input?.is_at_me ? '是' : '否'" />
            <MetricCard label="社交 ping" :value="socialPingSummary" />
            <MetricCard label="上下文" :value="payload.prompt_summary?.context_messages || 0" />
            <MetricCard label="System 字符" :value="payload.prompt_summary?.system_prompt_chars || 0" />
            <MetricCard label="Trace 阶段" :value="traceStages.length" />
            <MetricCard label="期望" :value="expectationSummary" />
          </div>
          <div class="run-summary">
            <strong>{{ runVerdict }}</strong>
            <span>{{ runSummary }}</span>
          </div>
          <div v-if="diagnostics" class="diagnostic-advice" :class="diagnosticLevelClass(diagnostics.level)">
            <div class="diagnostic-head">
              <strong>{{ diagnosticLevelLabel(diagnostics.level) }}</strong>
              <span>{{ diagnostics.summary }}</span>
            </div>
            <div v-if="diagnostics.factors?.length" class="diagnostic-factors">
              <span
                v-for="factor in diagnostics.factors"
                :key="factor.key"
                :class="diagnosticLevelClass(factor.level)"
                :title="factor.detail || factor.key"
              >
                <strong>{{ factor.label }}</strong>
                {{ formatDiagnosticValue(factor.value) }}
                <small v-if="factor.detail">{{ factor.detail }}</small>
              </span>
            </div>
            <div v-if="diagnostics.next_actions?.length" class="diagnostic-actions">
              <div v-for="action in diagnostics.next_actions" :key="action.key">
                <strong>{{ action.label }}</strong>
                <span>{{ action.detail }}</span>
              </div>
            </div>
          </div>
          <div v-if="presence" class="detail-strip">
            <span>在场感 p={{ formatNumber(presence.probability) }}</span>
            <span>兴趣 {{ formatNumber(presence.interest) }}</span>
            <span>{{ presence.interrupting ? "避免插嘴" : "可接话" }}</span>
            <span v-if="presence.primary_reason">主因 {{ formatPresenceReason(presence.primary_reason) }}</span>
          </div>
          <div v-if="presence?.human_summary" class="social-summary">{{ presence.human_summary }}</div>
          <div v-if="presenceCues.length" class="social-cues">
            <span
              v-for="cue in presenceCues"
              :key="cue.key"
              class="status-badge"
              :class="cueClass(cue.impact)"
              :title="cue.key"
            >
              {{ cue.label }}
            </span>
          </div>
          <div v-if="relationship" class="detail-strip">
            <span>关系 {{ relationshipLabel(relationship.state?.level) }}</span>
            <span>依据 {{ relationshipEvidenceLabel(relationship.explain?.evidence_level) }}</span>
            <span>净影响 {{ formatSigned(relationship.explain?.net_delta) }}</span>
            <span v-if="relationship.simulated_override">临时覆盖</span>
            <span v-if="relationshipSignals">{{ relationshipSignals }}</span>
          </div>
          <div v-if="userProfile" class="detail-strip">
            <span>画像 {{ profileRelationshipLabel(userProfile.quality?.relationship) }}</span>
            <span>置信 {{ formatNumber(userProfile.quality?.confidence) }}</span>
            <span>新鲜 {{ profileFreshnessLabel(userProfile.quality?.freshness) }}</span>
            <span>消息 {{ userProfile.message_count ?? 0 }}</span>
            <span>证据 {{ profileEvidenceLabel(userProfile.evidence?.evidence_level) }}</span>
            <span>{{ userProfile.prompt_applied ? "已进 Prompt" : "未进 Prompt" }}</span>
            <span v-if="userProfile.traits?.length">特征 {{ userProfile.traits.join(" / ") }}</span>
            <span v-if="userProfile.topics?.length">话题 {{ userProfile.topics.slice(0, 3).join(" / ") }}</span>
          </div>
          <div v-if="promptContext" class="detail-strip">
            <span>上下文 {{ promptContext.message_count ?? 0 }} / {{ promptContext.max_context ?? 0 }}</span>
            <span>用户 {{ promptContext.recent_user_count ?? 0 }}</span>
            <span>Bot {{ promptContext.recent_assistant_count ?? 0 }}</span>
            <span>模块 {{ promptContext.section_count ?? 0 }}</span>
            <span>System {{ promptContext.system_prompt_chars ?? 0 }}</span>
            <span v-if="promptContext.applied_sections?.length">已注入 {{ promptContext.applied_sections.slice(0, 6).join(" / ") }}</span>
          </div>
          <div v-if="emotion" class="detail-strip">
            <span>情绪 {{ emotionStyleLabel(emotion.style?.response_style) }}</span>
            <span>愉悦 {{ formatNumber(emotion.state?.valence) }}</span>
            <span>兴奋 {{ formatNumber(emotion.state?.arousal) }}</span>
            <span>支配 {{ formatNumber(emotion.state?.dominance) }}</span>
            <span>{{ emotion.prompt_applied ? "已进 Prompt" : "未进 Prompt" }}</span>
            <span>{{ emotion.style_applied ? "已修正风格" : "未修正风格" }}</span>
            <span v-if="emotion.max_tokens">Token {{ emotion.max_tokens.base ?? 0 }}{{ formatSigned(emotion.max_tokens.delta) }}={{ emotion.max_tokens.final ?? 0 }}</span>
          </div>
          <div v-if="memoryRecall" class="detail-strip">
            <span>RAG {{ memoryRecall.used ? "已召回" : "未召回" }}</span>
            <span>命中 {{ memoryRecall.hit_count ?? 0 }}</span>
            <span>最高 {{ formatNumber(memoryRecall.top_score) }}</span>
            <span>原文 {{ memoryRecall.lexical_hit_count ?? 0 }}</span>
            <span>精确 {{ memoryRecall.exact_hit_count ?? 0 }}</span>
            <span v-if="memoryRecall.retrieval_sources?.length">来源 {{ memoryRecall.retrieval_sources.join(" / ") }}</span>
            <span v-if="memoryRecall.top_results?.[0]?.preview">首条 {{ memoryRecall.top_results[0].preview }}</span>
          </div>
          <div v-if="replyDelivery" class="detail-strip">
            <span>引用 {{ replyDelivery.quote ? "是" : "否" }}</span>
            <span>{{ formatQuoteReason(replyDelivery.quote_reason) }}</span>
            <span>段数 {{ replyDelivery.part_count ?? 0 }}</span>
            <span>单元 {{ formatUnitTypes(replyDelivery.unit_types) }}</span>
            <span v-if="replyDelivery.sticker_selected">表情 {{ formatStickerPlacement(replyDelivery.sticker_placement) }}</span>
            <span v-if="replyDelivery.used_recall">撤回修正</span>
            <span v-if="replyDelivery.used_typo">错字修正</span>
          </div>
          <div v-if="replyNovelty" class="detail-strip novelty-strip">
            <span>复读 {{ replyNoveltyActionLabel(replyNovelty.action) }}</span>
            <span>原因 {{ replyNoveltyReasonLabel(replyNovelty.reason) }}</span>
            <span>相似 {{ formatNumber(replyNovelty.max_similarity) }}</span>
            <span>阈值 {{ formatNumber(replyNovelty.threshold || replyNovelty.hard_threshold || replyNovelty.soft_threshold) }}</span>
            <span>历史 {{ replyNovelty.recent_count ?? 0 }}</span>
            <span v-if="replyNovelty.matched_preview">命中 {{ replyNovelty.matched_preview }}</span>
          </div>
          <div v-if="stickerDecision" class="detail-strip sticker-strip">
            <span>表情 {{ stickerActionLabel(stickerDecision.action) }}</span>
            <span>结果 {{ stickerResultLabel(stickerDecision.result || stickerDecision.failure_reason) }}</span>
            <span>模式 {{ stickerRequestModeLabel(stickerDecision.request_mode) }}</span>
            <span>候选 {{ stickerDecision.candidate_count ?? 0 }}</span>
            <span v-if="stickerDecision.reason">原因 {{ stickerDecision.reason }}</span>
            <span v-if="stickerDecision.selected_candidate?.summary">选中 {{ stickerDecision.selected_candidate.summary }}</span>
            <span v-if="stickerDecision.envelope_error">结构化 {{ stickerDecision.envelope_error }}</span>
          </div>
          <div v-if="taboo" class="detail-strip taboo-strip">
            <span>禁区 {{ taboo.matched ? `#${(taboo.matched_index ?? 0) + 1}` : "未命中" }}</span>
            <span>动作 {{ tabooActionLabel(taboo.action) }}</span>
            <span>关键词 {{ formatKeywords(taboo.matched_keywords) }}</span>
            <span v-if="taboo.reason">原因 {{ taboo.reason }}</span>
            <span v-if="taboo.conflict_count">冲突 {{ taboo.conflict_count }}</span>
          </div>
          <div v-if="deliveryTiming" class="detail-strip">
            <span>节奏 {{ deliveryTiming.priority || "-" }} / {{ deliveryTiming.status || "-" }}</span>
            <span>预计 {{ formatMs(deliveryTiming.estimated_delay_ms) }}</span>
            <span>实际 {{ formatMs(deliveryTiming.actual_delay_ms) }}</span>
            <span>队列 {{ formatMs(deliveryTiming.queue_wait_ms) }}</span>
            <span>单元 {{ formatTimingUnitTypes(deliveryTiming.plan_units) }}</span>
            <span v-if="deliveryTiming.stale">过时 {{ deliveryTiming.stale_reason || "是" }}</span>
          </div>
          <div v-if="multimodalItems.length" class="detail-strip">
            <span v-for="item in multimodalItems" :key="item.key" :title="item.detail">
              {{ item.label }}
            </span>
          </div>
          <div v-if="payload.input" class="detail-strip">
            <span>输入 {{ payload.input.message_type || "-" }}</span>
            <span>上下文 {{ payload.input.context_key || "-" }}</span>
            <span>段 {{ (payload.input.segment_types || []).join(" / ") || "-" }}</span>
            <span v-if="payload.input.interaction_history_count">互动历史 {{ payload.input.interaction_history_count }}</span>
            <span v-if="payload.input.raw_message">{{ payload.input.raw_message }}</span>
          </div>
          <div v-if="expectationChecks.length" class="expectation-panel">
            <h3>预设期望</h3>
            <div
              v-for="check in expectationChecks"
              :key="check.key"
              class="expectation-row"
              :class="{ passed: check.passed, failed: !check.passed }"
            >
              <strong>{{ check.passed ? "通过" : "偏离" }}</strong>
              <span>{{ check.label }}</span>
              <code>{{ formatExpectation(check.actual) }}</code>
            </div>
          </div>
          <div v-if="sideEffects" class="side-effects">
            <h3>副作用边界</h3>
            <div class="effect-group">
              <strong>已抑制</strong>
              <span v-for="item in sideEffects.suppressed || []" :key="item.key" :title="item.detail">{{ item.label }}</span>
            </div>
            <div class="effect-group">
              <strong>已回滚</strong>
              <span v-for="item in sideEffects.restored || []" :key="item.key" :title="item.detail">{{ item.label }}</span>
            </div>
            <div class="effect-group">
              <strong>仍记录</strong>
              <span v-for="item in sideEffects.recorded || []" :key="item.key" :title="item.detail">{{ item.label }}</span>
            </div>
          </div>
          <h3>回复</h3>
          <pre>{{ payload.reply || "没有生成回复。请查看 trace 中的 stopped 阶段。" }}</pre>
          <h3>投递计划</h3>
          <pre>{{ JSON.stringify(payload.delivery_timing || {}, null, 2) }}</pre>
          <h3>RAG 召回</h3>
          <DataTable :columns="memoryColumns" :rows="payload.memory_debug || []" row-key="memory_id">
            <template #memory_id="{ row }">
              <span>{{ row.memory_id || "无 ID" }}</span>
              <span v-if="row.why_recalled?.lexical_match" class="status-badge ok">原文</span>
            </template>
            <template #score="{ row }">{{ formatScore(row.why_recalled?.score ?? row.score) }}</template>
            <template #recall="{ row }">
              <strong>{{ recallVerdict(row) }}</strong>
              <span class="table-subtext">{{ recallDetail(row) }}</span>
            </template>
            <template #source="{ row }">{{ sourceLabel(row.source || row.metadata?.source) }}</template>
            <template #doc="{ row }"><pre>{{ row.doc || row.document || row }}</pre></template>
          </DataTable>
          <h3>Trace 阶段</h3>
          <DataTable :columns="traceColumns" :rows="traceStages">
            <template #error="{ row }"><span class="error-text">{{ row.error || "-" }}</span></template>
          </DataTable>
        </div>
        <pre v-else>{{ result || "尚未模拟" }}</pre>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { api, postJson } from "../api/client";
import { useClipboardResult } from "../composables/useClipboardResult";
import DataTable from "../ui/DataTable.vue";
import FormField from "../ui/FormField.vue";
import MetricCard from "../ui/MetricCard.vue";

type SimulatePreset = {
  id: string;
  title: string;
  description: string;
  payload: Record<string, any>;
  expectations?: Record<string, any>;
};
type SimulateExpectationSchema = {
  sections?: Array<{
    key: string;
    label: string;
    fields?: Array<{ path: string; description?: string }>;
  }>;
  reply_delivery_fields?: string[];
  reply_novelty_fields?: string[];
  sticker_decision_fields?: string[];
  emotion_fields?: string[];
  memory_recall_fields?: string[];
  user_profile_fields?: string[];
  prompt_context_fields?: string[];
  multimodal_channels?: string[];
  multimodal_fields?: string[];
  taboo_fields?: string[];
  command_fields?: string[];
  addressing_fields?: string[];
  diagnostics_fields?: string[];
};

const form = reactive({
  message: "",
  user_id: "10001",
  group_id: "",
  persona: "",
  nickname: "Simulator",
  self_id: "99999",
  at_bot: false,
  notice_poke: false,
  affinity_level: ""
});
const historyJson = ref("[]");
const interactionHistoryJson = ref("[]");
const imageUrlsText = ref("");
const recordUrl = ref("");
const result = ref("");
const payload = ref<any | null>(null);
const error = ref("");
const actionResult = ref("");
const { copyText } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择结果");
const running = ref(false);
const selectedPreset = ref("");
const presetQuery = ref("");
const presetFilter = ref<"all" | "group" | "private" | "affinity">("all");
const selectedExpectations = ref<Record<string, any>>({});
const presets = ref<SimulatePreset[]>([]);
const expectationSchema = ref<SimulateExpectationSchema>({});
const personas = ref<string[]>([]);
const selectedPersona = ref("");
const memoryColumns = [
  { key: "memory_id", label: "记忆" },
  { key: "score", label: "分数" },
  { key: "recall", label: "召回" },
  { key: "source", label: "来源" },
  { key: "doc", label: "内容" }
];
const traceColumns = [
  { key: "name", label: "阶段" },
  { key: "status", label: "状态" },
  { key: "elapsed_ms", label: "耗时 ms" },
  { key: "error", label: "错误" }
];
const traceStages = computed(() => payload.value?.trace?.stages || []);
const imageUrls = computed(() => {
  return imageUrlsText.value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
});
const hasRunnableInput = computed(() => {
  return Boolean(
    form.message.trim()
    || imageUrls.value.length
    || recordUrl.value.trim()
    || (form.group_id && form.at_bot)
    || form.notice_poke
  );
});
const presence = computed(() => {
  const value = payload.value?.decision?.presence;
  return value && Object.keys(value).length ? value : null;
});
const presenceCues = computed(() => {
  const cues = presence.value?.social_cues;
  if (Array.isArray(cues) && cues.length) return cues;
  const reasons = presence.value?.reasons;
  if (!Array.isArray(reasons)) return [];
  return reasons.slice(0, 4).map((reason: string) => ({
    key: reason,
    label: formatPresenceReason(reason),
    impact: "neutral"
  }));
});
const replyDelivery = computed(() => {
  const value = payload.value?.decision?.reply_delivery;
  return value && Object.keys(value).length ? value : null;
});
const replyNovelty = computed(() => {
  const value = payload.value?.decision?.reply_novelty;
  return value && Object.keys(value).length ? value : null;
});
const replyNoveltySummary = computed(() => {
  if (!replyNovelty.value) return "-";
  const reason = replyNoveltyReasonLabel(replyNovelty.value.reason);
  const score = formatNumber(replyNovelty.value.max_similarity);
  return `${replyNoveltyActionLabel(replyNovelty.value.action)} / ${reason} / ${score}`;
});
const stickerDecision = computed(() => {
  const value = payload.value?.decision?.sticker_decision;
  return value && Object.keys(value).length ? value : null;
});
const stickerDecisionSummary = computed(() => {
  if (!stickerDecision.value?.has_plan) return "-";
  const action = stickerActionLabel(stickerDecision.value.action);
  const resultValue = stickerDecision.value.result || stickerDecision.value.failure_reason;
  return `${action} / ${stickerResultLabel(resultValue)} / 候选${stickerDecision.value.candidate_count ?? 0}`;
});
const taboo = computed(() => {
  const value = payload.value?.decision?.taboo;
  return value && Object.keys(value).length ? value : null;
});
const tabooSummary = computed(() => {
  if (!taboo.value?.matched) return "-";
  const index = Number(taboo.value.matched_index);
  const rule = Number.isFinite(index) ? `#${index + 1}` : "#?";
  return `${rule} / ${tabooActionLabel(taboo.value.action)}`;
});
const deliveryTiming = computed(() => {
  const value = payload.value?.delivery_timing;
  return value && Object.keys(value).length ? value : null;
});
const relationship = computed(() => {
  const value = payload.value?.relationship;
  return value && Object.keys(value).length ? value : null;
});
const relationshipSummary = computed(() => {
  if (!relationship.value) return "-";
  return `${relationshipLabel(relationship.value.state?.level)}${relationship.value.simulated_override ? " / 覆盖" : ""}`;
});
const relationshipSignals = computed(() => {
  const signals = relationship.value?.explain?.signals;
  return Array.isArray(signals) ? signals.map(relationshipSignalLabel).join(" / ") : "";
});
const userProfile = computed(() => {
  const value = payload.value?.decision?.user_profile;
  return value && Object.keys(value).length ? value : null;
});
const userProfileSummary = computed(() => {
  if (!userProfile.value?.available) return "-";
  const bits = [
    profileRelationshipLabel(userProfile.value.quality?.relationship),
    `conf=${formatNumber(userProfile.value.quality?.confidence)}`,
    userProfile.value.prompt_applied ? "prompt" : "",
    profileEvidenceLabel(userProfile.value.evidence?.evidence_level)
  ].filter(Boolean);
  return bits.join(" / ");
});
const promptContext = computed(() => {
  const value = payload.value?.decision?.prompt_context;
  return value && Object.keys(value).length ? value : null;
});
const promptContextSummary = computed(() => {
  if (!promptContext.value) return "-";
  const sections = Array.isArray(promptContext.value.applied_sections)
    ? promptContext.value.applied_sections.slice(0, 3).join("+")
    : "";
  return `${promptContext.value.message_count ?? 0}条 / ${promptContext.value.section_count ?? 0}块${sections ? ` / ${sections}` : ""}`;
});
const emotion = computed(() => {
  const value = payload.value?.decision?.emotion;
  return value && Object.keys(value).length ? value : null;
});
const emotionSummary = computed(() => {
  if (!emotion.value) return "-";
  const bits = [
    emotionStyleLabel(emotion.value.style?.response_style),
    `v=${formatNumber(emotion.value.state?.valence)}`,
    `a=${formatNumber(emotion.value.state?.arousal)}`,
    emotion.value.prompt_applied ? "prompt" : "",
    emotion.value.style_applied ? "style" : ""
  ].filter(Boolean);
  return bits.join(" / ");
});
const memoryRecall = computed(() => {
  const value = payload.value?.decision?.memory_recall;
  return value && Object.keys(value).length ? value : null;
});
const memoryRecallSummary = computed(() => {
  if (!memoryRecall.value) return "-";
  if (!memoryRecall.value.used) return "0";
  const sources = Array.isArray(memoryRecall.value.retrieval_sources)
    ? memoryRecall.value.retrieval_sources.slice(0, 2).join("+")
    : "";
  return `${memoryRecall.value.hit_count ?? 0} / ${formatNumber(memoryRecall.value.top_score)}${sources ? ` / ${sources}` : ""}`;
});
const deliveryTimingSummary = computed(() => {
  if (!deliveryTiming.value) return "-";
  return `${deliveryTiming.value.priority || "-"} / ${formatMs(deliveryTiming.value.estimated_delay_ms)}`;
});
const multimodal = computed(() => {
  const value = payload.value?.decision?.multimodal;
  return value && Object.keys(value).length ? value : null;
});
const multimodalItems = computed(() => {
  const value = multimodal.value;
  if (!value) return [];
  return Object.entries(value).map(([channel, detail]) => {
    const data = (detail || {}) as Record<string, unknown>;
    const status = String(data.status || "unknown");
    const extra = [
      data.text_chars !== undefined ? `${data.text_chars} 字` : "",
      data.count !== undefined ? `${data.count} 张` : "",
      data.descriptions !== undefined ? `${data.descriptions} 个描述` : ""
    ].filter(Boolean).join(" / ");
    return {
      key: channel,
      label: `${formatMultimodalChannel(channel)} ${formatMultimodalStatus(status)}${extra ? ` · ${extra}` : ""}`,
      detail: JSON.stringify(data)
    };
  });
});
const multimodalSummary = computed(() => {
  if (!multimodalItems.value.length) return "-";
  return multimodalItems.value.map((item) => item.label).join(" / ");
});
const socialPingSummary = computed(() => {
  const addressing = payload.value?.decision?.addressing || payload.value?.trace?.final_decision?.addressing || {};
  if (!addressing.social_ping) return "-";
  const kind = addressing.notice_origin?.kind || addressing.mention_only_kind || "ping";
  return `${kind}${addressing.notice_origin?.target_matches_self ? " / 指向Bot" : ""}`;
});
const sideEffects = computed(() => {
  const value = payload.value?.side_effects;
  return value && Object.keys(value).length ? value : null;
});
const diagnostics = computed(() => {
  const value = payload.value?.diagnostics;
  return value && Object.keys(value).length ? value : null;
});
const expectationChecks = computed(() => payload.value?.expectation_checks?.checks || []);
const expectationSummary = computed(() => {
  const checks = expectationChecks.value;
  if (!checks.length) return "-";
  const passed = checks.filter((check: any) => check.passed).length;
  return `${passed}/${checks.length}`;
});
const runVerdict = computed(() => {
  if (!payload.value) return "";
  if (expectationChecks.value.some((check: any) => !check.passed)) return "预设期望偏离";
  if (payload.value.decision?.stopped) return "已按策略停止";
  if (payload.value.reply) return "已生成回复";
  return "无回复";
});
const runSummary = computed(() => {
  if (!payload.value) return "";
  const bits = [
    `persona=${payload.value.decision?.persona || "-"}`,
    `stop=${payload.value.decision?.stop_reason || "-"}`,
    `delivery=${replyDelivery.value?.delivery_type || payload.value.decision?.delivery_type || "-"}`,
    `novelty=${replyNoveltySummary.value}`,
    `sticker=${stickerDecisionSummary.value}`,
    `taboo=${tabooSummary.value}`,
    `relationship=${relationshipSummary.value}`,
    `profile=${userProfileSummary.value}`,
    `emotion=${emotionSummary.value}`,
    `memory=${memoryRecallSummary.value}`,
    `timing=${deliveryTimingSummary.value}`,
    `multimodal=${multimodalSummary.value}`,
    `presence=${formatNumber(presence.value?.probability)}`
  ];
  return bits.join(" / ");
});
const filteredPresets = computed(() => {
  const text = presetQuery.value.trim().toLowerCase();
  return presets.value.filter((preset) => {
    const payload = preset.payload || {};
    if (presetFilter.value === "group" && !payload.group_id) return false;
    if (presetFilter.value === "private" && payload.group_id) return false;
    if (presetFilter.value === "affinity" && !payload.affinity_level) return false;
    if (!text) return true;
    return `${preset.id} ${preset.title} ${preset.description}`.toLowerCase().includes(text);
  });
});
const schemaFieldLabels = computed(() => {
  const labels: Record<string, string> = {};
  for (const section of expectationSchema.value.sections || []) {
    for (const field of section.fields || []) {
      labels[field.path] = expectationPathShortLabel(field.path);
    }
  }
  return labels;
});
const selectedExpectationItems = computed(() => {
  return collectExpectationPaths(selectedExpectations.value).map((path) => ({
    path,
    label: expectationPathLabel(path)
  }));
});
function formatNumber(value: unknown) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "-";
}
function formatScore(value: unknown) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(3) : "-";
}
function formatMs(value: unknown) {
  const n = Number(value);
  return Number.isFinite(n) ? `${Math.round(n)}ms` : "-";
}
function formatExpectation(value: unknown) {
  if (Array.isArray(value)) return value.join(" / ");
  if (typeof value === "object" && value !== null) return JSON.stringify(value);
  if (value === "") return "(空)";
  return String(value);
}
function formatDiagnosticValue(value: unknown) {
  if (typeof value === "number") return Number.isInteger(value) ? String(value) : value.toFixed(2);
  if (value === true) return "是";
  if (value === false) return "否";
  return String(value ?? "-");
}
function diagnosticLevelLabel(value: unknown) {
  const labels: Record<string, string> = {
    ok: "正常",
    info: "提示",
    warning: "注意",
    error: "异常"
  };
  const key = String(value || "");
  return labels[key] || key || "诊断";
}
function diagnosticLevelClass(value: unknown) {
  const key = String(value || "");
  if (key === "ok") return "ok";
  if (key === "warning") return "degraded";
  if (key === "error") return "down";
  return "pending";
}
function collectExpectationPaths(value: unknown, prefix = ""): string[] {
  if (!value || typeof value !== "object") return prefix ? [prefix] : [];
  if (Array.isArray(value)) return prefix ? [prefix] : [];
  const item = value as Record<string, unknown>;
  const keys = Object.keys(item);
  if (!keys.length) return [];
  if (prefix && keys.every((key) => key === "min" || key === "max")) return [prefix];
  return keys.flatMap((key) => collectExpectationPaths(item[key], prefix ? `${prefix}.${key}` : key));
}
function expectationPathLabel(path: string) {
  if (schemaFieldLabels.value[path]) return schemaFieldLabels.value[path];
  if (path.startsWith("trace.final_decision.reply_delivery.") && expectationSchema.value.reply_delivery_fields?.includes(path.split(".").pop() || "")) {
    return `Trace 回复投递: ${replyDeliveryFieldLabel(path.split(".").pop() || "")}`;
  }
  if (path.startsWith("trace.final_decision.reply_novelty.")) {
    return `Trace 复读保护: ${replyNoveltyFieldLabel(path.replace("trace.final_decision.reply_novelty.", ""))}`;
  }
  if (path.startsWith("trace.final_decision.sticker_decision.")) {
    return `Trace 表情决策: ${stickerDecisionFieldLabel(path.replace("trace.final_decision.sticker_decision.", ""))}`;
  }
  if (path.startsWith("trace.final_decision.emotion.")) {
    return `Trace 情绪: ${emotionFieldLabel(path.replace("trace.final_decision.emotion.", ""))}`;
  }
  if (path.startsWith("trace.final_decision.memory_recall.")) {
    return `Trace RAG: ${memoryRecallFieldLabel(path.replace("trace.final_decision.memory_recall.", ""))}`;
  }
  if (path.startsWith("trace.final_decision.user_profile.")) {
    return `Trace 画像: ${userProfileFieldLabel(path.replace("trace.final_decision.user_profile.", ""))}`;
  }
  if (path.startsWith("trace.final_decision.prompt_context.")) {
    return `Trace Prompt: ${promptContextFieldLabel(path.replace("trace.final_decision.prompt_context.", ""))}`;
  }
  if (path.startsWith("trace.final_decision.multimodal.")) {
    return `Trace 多模态: ${multimodalPathLabel(path.replace("trace.final_decision.multimodal.", ""))}`;
  }
  if (path.startsWith("trace.final_decision.taboo.")) {
    return `Trace 禁区: ${tabooFieldLabel(path.replace("trace.final_decision.taboo.", ""))}`;
  }
  if (path.startsWith("trace.final_decision.command.")) {
    return `Trace 命令决策: ${commandDecisionFieldLabel(path.replace("trace.final_decision.command.", ""))}`;
  }
  if (path.startsWith("trace.final_decision.")) {
    return `Trace: ${decisionFieldLabel(path.split(".").pop() || "")}`;
  }
  const replyField = path.split(".reply_delivery.")[1];
  if (replyField && expectationSchema.value.reply_delivery_fields?.includes(replyField)) {
    return `回复投递: ${replyDeliveryFieldLabel(replyField)}`;
  }
  const noveltyField = path.split(".reply_novelty.")[1];
  if (noveltyField && expectationSchema.value.reply_novelty_fields?.includes(noveltyField)) {
    return `复读保护: ${replyNoveltyFieldLabel(noveltyField)}`;
  }
  const stickerDecisionField = path.split(".sticker_decision.")[1];
  if (stickerDecisionField && expectationSchema.value.sticker_decision_fields?.includes(stickerDecisionField)) {
    return `表情决策: ${stickerDecisionFieldLabel(stickerDecisionField)}`;
  }
  const emotionField = path.split(".emotion.")[1];
  if (emotionField && expectationSchema.value.emotion_fields?.includes(emotionField)) {
    return `情绪: ${emotionFieldLabel(emotionField)}`;
  }
  const memoryRecallField = path.split(".memory_recall.")[1];
  if (memoryRecallField && expectationSchema.value.memory_recall_fields?.includes(memoryRecallField)) {
    return `RAG: ${memoryRecallFieldLabel(memoryRecallField)}`;
  }
  const userProfileField = path.split(".user_profile.")[1];
  if (userProfileField && expectationSchema.value.user_profile_fields?.includes(userProfileField)) {
    return `画像: ${userProfileFieldLabel(userProfileField)}`;
  }
  const promptContextField = path.split(".prompt_context.")[1];
  if (promptContextField && expectationSchema.value.prompt_context_fields?.includes(promptContextField)) {
    return `Prompt: ${promptContextFieldLabel(promptContextField)}`;
  }
  if (path.startsWith("decision.multimodal.")) {
    return `多模态: ${multimodalPathLabel(path.replace("decision.multimodal.", ""))}`;
  }
  if (path.startsWith("decision.taboo.")) {
    return `禁区: ${tabooFieldLabel(path.replace("decision.taboo.", ""))}`;
  }
  if (path.startsWith("decision.command.")) {
    return `命令决策: ${commandDecisionFieldLabel(path.replace("decision.command.", ""))}`;
  }
  if (path.startsWith("diagnostics.")) {
    return `调试诊断: ${diagnosticsFieldLabel(path.replace("diagnostics.", ""))}`;
  }
  if (path.startsWith("decision.")) {
    return `决策: ${decisionFieldLabel(path.split(".").pop() || "")}`;
  }
  if (path.startsWith("delivery_timing.")) {
    return `发送节奏: ${deliveryTimingFieldLabel(path.split(".").pop() || "")}`;
  }
  if (path.startsWith("relationship.")) {
    return `关系: ${relationshipFieldLabel(path.replace("relationship.", ""))}`;
  }
  const presenceField = path.split("presence.")[1];
  if (presenceField) return `在场感: ${presenceFieldLabel(presenceField)}`;
  return path;
}
function expectationPathShortLabel(path: string) {
  const labels: Record<string, string> = {
    "input.message_type": "输入: 消息类型",
    "input.is_at_me": "输入: 是否 @Bot",
    "input.interaction_history_count": "输入: 群互动历史",
    "input.social_ping": "输入: 社交 ping",
    "input.mention_only_kind": "输入: 唤醒类型",
    "decision.stopped": "决策: 是否停止",
    "decision.skip_reply": "决策: 跳过回复",
    "decision.delivered": "决策: 完成投递",
    "decision.should_reply": "决策: 是否回复",
    "decision.stop_reason": "决策: 停止原因",
    "decision.stop_reason_in": "决策: 停止原因范围",
    "decision.delivery_type": "决策: 投递类型",
    "decision.persona": "决策: 人设",
    "decision.reply_delivery": "决策: 回复投递",
    "decision.reply_novelty": "决策: 复读保护",
    "decision.sticker_decision": "决策: 表情包",
    "decision.emotion": "决策: 情绪",
    "decision.memory_recall": "决策: RAG 召回",
    "decision.user_profile": "决策: 用户画像",
    "decision.prompt_context": "决策: Prompt 上下文",
    "decision.multimodal": "决策: 多模态",
    "decision.taboo": "决策: 禁区命中",
    "decision.command": "决策: 命令路由",
    "decision.addressing": "决策: 寻址证据",
    "presence.probability": "在场感: 概率范围",
    "presence.interest": "在场感: 兴趣范围",
    "presence.interrupting": "在场感: 插嘴判断",
    "presence.reasons_contains": "在场感: 原因包含",
    "relationship.state.level": "关系: 等级",
    "relationship.explain.evidence_level": "关系: 证据等级",
    "relationship.simulated_override": "关系: 临时覆盖",
    "delivery_timing.priority": "发送节奏: 优先级",
    "delivery_timing.status": "发送节奏: 状态",
    "delivery_timing.stale": "发送节奏: 是否过时",
    "delivery_timing.estimated_delay_ms": "发送节奏: 预计等待",
    "delivery_timing.actual_delay_ms": "发送节奏: 实际等待",
    "delivery_timing.queue_wait_ms": "发送节奏: 队列等待",
    "delivery_timing.unit_types_contains": "发送节奏: 单元包含",
    "trace.final_decision": "Trace: 最终决策",
    "diagnostics.level": "调试诊断: 级别",
    "diagnostics.primary_reason": "调试诊断: 主因",
    "diagnostics.summary_contains": "调试诊断: 摘要",
    "diagnostics.action_keys_contains": "调试诊断: 建议",
    "diagnostics.factor_keys_contains": "调试诊断: 因子"
  };
  return labels[path] || path;
}
function replyDeliveryFieldLabel(field: string) {
  const labels: Record<string, string> = {
    delivery_type: "投递类型",
    delivery_type_in: "投递类型范围",
    quote: "是否引用",
    quote_reason: "引用原因",
    part_count: "消息段数",
    unit_types_contains: "投递单元",
    sticker_selected: "是否选表情",
    sticker_placement: "表情位置",
    used_recall: "撤回修正",
    used_typo: "错字修正"
  };
  return labels[field] || field;
}
function decisionFieldLabel(field: string) {
  const labels: Record<string, string> = {
    stopped: "是否停止",
    skip_reply: "跳过回复",
    delivered: "完成投递",
    stop_reason: "停止原因",
    stop_reason_in: "停止原因范围",
    delivery_type: "投递类型",
    persona: "人设",
    reply_novelty: "复读保护",
    sticker_decision: "表情包",
    emotion: "情绪",
    memory_recall: "RAG 召回",
    user_profile: "用户画像",
    prompt_context: "Prompt 上下文",
    multimodal: "多模态",
    taboo: "禁区命中"
  };
  return labels[field] || field;
}
function replyNoveltyFieldLabel(field: string) {
  const labels: Record<string, string> = {
    enabled: "是否启用",
    action: "动作",
    action_in: "动作范围",
    reason: "原因",
    reason_in: "原因范围",
    max_similarity: "最高相似度",
    threshold: "触发阈值",
    recent_count: "历史样本数",
    matched_preview_contains: "命中预览",
    recent_previews_contains: "最近样本"
  };
  return labels[field] || field;
}
function replyNoveltyActionLabel(value: unknown) {
  const labels: Record<string, string> = {
    keep: "保留",
    warn: "提醒改写",
    skip: "跳过"
  };
  const key = String(value || "");
  return labels[key] || key || "-";
}
function replyNoveltyReasonLabel(value: unknown) {
  const labels: Record<string, string> = {
    disabled: "未启用",
    too_short: "短回复放行",
    hard_duplicate: "硬重复",
    similar_recent_reply: "接近近期发言"
  };
  const key = String(value || "");
  return labels[key] || key || "无";
}
function stickerDecisionFieldLabel(field: string) {
  const labels: Record<string, string> = {
    has_plan: "是否有计划",
    envelope_required: "结构化回复",
    envelope_error: "结构化错误",
    plan_type: "计划类型",
    result: "结果",
    result_in: "结果范围",
    failure_reason: "失败原因",
    failure_reason_in: "失败原因范围",
    request_mode: "请求模式",
    action: "动作",
    action_in: "动作范围",
    placement: "位置",
    confidence: "置信度",
    candidate_count: "候选数量",
    selected_summary_contains: "选中摘要",
    top_match_reasons_contains: "候选命中原因",
    top_candidate_sources_contains: "候选来源"
  };
  return labels[field] || field;
}
function stickerActionLabel(value: unknown) {
  const labels: Record<string, string> = {
    send: "发送",
    none: "不发"
  };
  const key = String(value || "");
  return labels[key] || key || "-";
}
function stickerResultLabel(value: unknown) {
  const labels: Record<string, string> = {
    preselected: "已预召回",
    selected: "已选择",
    sent: "已发送",
    skipped: "已跳过",
    failed: "发送失败",
    no_candidates: "无候选",
    probability_gate: "概率门控",
    llm_none: "LLM 未选",
    invalid_json: "结构化失败",
    invalid_hash: "候选无效",
    file_missing: "文件缺失"
  };
  const key = String(value || "");
  return labels[key] || key || "-";
}
function stickerRequestModeLabel(value: unknown) {
  const labels: Record<string, string> = {
    implicit: "自然接梗",
    generic_request: "泛要表情",
    explicit_name: "点名表情",
    explicit_intent: "明确想要",
    command: "命令"
  };
  const key = String(value || "");
  return labels[key] || key || "-";
}
function emotionFieldLabel(field: string) {
  const labels: Record<string, string> = {
    enabled: "是否启用",
    update_on_message: "消息更新",
    prompt_enabled: "允许 Prompt",
    prompt_applied: "已进 Prompt",
    style_modifiers_enabled: "允许风格修正",
    style_applied: "已修正风格",
    delivery_timing_enabled: "影响发送节奏",
    dry_run_update_suppressed: "Dry-run 抑制写入",
    "state.valence": "愉悦",
    "state.arousal": "兴奋",
    "state.dominance": "支配",
    "style.response_style_contains": "情绪标签",
    "style.max_tokens_delta": "Token 修正",
    "style.sticker_prob_multiplier": "表情倍率",
    prompt_chars: "Prompt 字符",
    "max_tokens.base": "Token 基准",
    "max_tokens.delta": "Token 修正",
    "max_tokens.final": "Token 最终"
  };
  return labels[field] || field;
}
function emotionStyleLabel(value: unknown) {
  const raw = String(value || "").trim();
  return raw.replace(/^当前情绪:\s*/, "") || "-";
}
function memoryRecallFieldLabel(field: string) {
  const labels: Record<string, string> = {
    used: "是否召回",
    hit_count: "召回条数",
    top_score: "最高分",
    lexical_hit_count: "原文命中数",
    exact_hit_count: "精确命中数",
    retrieval_sources_contains: "召回来源",
    top_preview_contains: "首条预览",
    top_source_type: "首条来源类型",
    top_importance: "首条重要度"
  };
  return labels[field] || field;
}
function userProfileFieldLabel(field: string) {
  const labels: Record<string, string> = {
    available: "是否有画像",
    prompt_applied: "已进 Prompt",
    prompt_chars: "Prompt 字符",
    message_count: "消息数",
    traits_contains: "特征包含",
    topics_contains: "话题包含",
    style_summary_contains: "风格摘要",
    bot_names_contains: "Bot 称呼",
    "quality.confidence": "置信度",
    "quality.freshness": "新鲜度",
    "quality.relationship": "熟悉度",
    "quality.signals_contains": "质量信号",
    "evidence.sample_count": "证据样本",
    "evidence.evidence_level": "证据等级"
  };
  return labels[field] || field;
}
function promptContextFieldLabel(field: string) {
  const labels: Record<string, string> = {
    system_prompt_chars: "System 字符",
    message_count: "上下文消息",
    recent_user_count: "用户消息",
    recent_assistant_count: "Bot 消息",
    max_context: "上下文上限",
    section_count: "模块数",
    applied_sections_contains: "已注入模块"
  };
  return labels[field] || field;
}
function profileRelationshipLabel(value: unknown) {
  const labels: Record<string, string> = {
    new: "新朋友",
    acquaintance: "认识",
    familiar: "熟悉",
    old_friend: "老熟人"
  };
  const key = String(value || "");
  return labels[key] || key || "-";
}
function profileFreshnessLabel(value: unknown) {
  const labels: Record<string, string> = {
    fresh: "新鲜",
    cooling: "变冷",
    stale: "过期",
    unknown: "未知"
  };
  const key = String(value || "");
  return labels[key] || key || "-";
}
function profileEvidenceLabel(value: unknown) {
  const labels: Record<string, string> = {
    none: "无",
    sparse: "较少",
    enough: "充足"
  };
  const key = String(value || "");
  return labels[key] || key || "-";
}
function tabooFieldLabel(field: string) {
  const labels: Record<string, string> = {
    matched: "是否命中",
    action: "动作",
    matched_index: "命中规则",
    matched_keywords_contains: "命中关键词",
    conflict_count: "冲突数量"
  };
  return labels[field] || field;
}
function tabooActionLabel(value: unknown) {
  const labels: Record<string, string> = {
    deflect: "岔开",
    ignore: "忽略"
  };
  const key = String(value || "");
  return labels[key] || key || "-";
}
function formatKeywords(value: unknown) {
  if (!Array.isArray(value) || !value.length) return "-";
  return value.map((item) => String(item)).join(" / ");
}
function deliveryTimingFieldLabel(field: string) {
  const labels: Record<string, string> = {
    priority: "优先级",
    status: "状态",
    stale: "是否过时",
    stale_reason: "过时原因",
    estimated_delay_ms: "预计等待",
    actual_delay_ms: "实际等待",
    queue_wait_ms: "队列等待",
    unit_types_contains: "单元包含"
  };
  return labels[field] || field;
}
function relationshipFieldLabel(field: string) {
  const labels: Record<string, string> = {
    "state.level": "等级",
    "explain.evidence_level": "证据等级",
    simulated_override: "临时覆盖"
  };
  return labels[field] || field;
}
function relationshipLabel(level: unknown) {
  const labels: Record<string, string> = {
    hostile: "敌对",
    cold: "冷淡",
    neutral: "普通",
    friendly: "友好",
    close: "亲近",
    trusted: "信任"
  };
  const key = String(level || "");
  return labels[key] || key || "-";
}
function relationshipEvidenceLabel(level: unknown) {
  const labels: Record<string, string> = { none: "无依据", sparse: "依据少", steady: "依据稳定", rich: "依据充分" };
  const key = String(level || "");
  return labels[key] || key || "-";
}
function relationshipSignalLabel(signal: unknown) {
  const labels: Record<string, string> = {
    no_events: "无事件",
    manual_override: "手动锁定",
    stale: "较久未更新",
    mixed_history: "正负混合",
    strong_positive: "明显亲近",
    strong_negative: "明显冷淡",
    simulated_override: "临时覆盖"
  };
  const key = String(signal || "");
  return labels[key] || key;
}
function formatSigned(value: unknown) {
  const n = Number(value);
  return Number.isFinite(n) ? `${n >= 0 ? "+" : ""}${n.toFixed(1)}` : "-";
}
function multimodalPathLabel(path: string) {
  const [channel, field = ""] = path.split(".");
  const fieldLabels: Record<string, string> = {
    status: "状态",
    status_in: "状态范围",
    reason: "原因",
    text_contains: "文本包含"
  };
  return `${formatMultimodalChannel(channel)} ${fieldLabels[field] || field || "详情"}`;
}
function formatMultimodalChannel(value: unknown) {
  const labels: Record<string, string> = {
    image: "图片",
    voice: "语音"
  };
  const key = String(value || "");
  return labels[key] || key || "多模态";
}
function formatMultimodalStatus(value: unknown) {
  const labels: Record<string, string> = {
    pending: "待处理",
    recognized: "已识别",
    unrecognized: "未识别",
    unavailable: "不可用",
    collect_only: "仅收集"
  };
  const key = String(value || "");
  return labels[key] || key || "-";
}
function presenceFieldLabel(field: string) {
  const labels: Record<string, string> = {
    probability: "概率范围",
    interest: "兴趣范围",
    interrupting: "插嘴判断",
    reasons_contains: "原因包含"
  };
  return labels[field] || field;
}
function commandDecisionFieldLabel(field: string) {
  const labels: Record<string, string> = {
    command: "命令",
    known: "是否已知",
    scope: "作用域",
    is_owner: "Owner 视角",
    addressed: "是否定向",
    args_present: "是否有参数",
    outcome: "结果",
    outcome_in: "结果范围",
    suggestion: "建议命令",
    denial_reason: "拒绝原因",
    error_type: "异常类型"
  };
  return labels[field] || field;
}
function diagnosticsFieldLabel(field: string) {
  const labels: Record<string, string> = {
    level: "级别",
    primary_reason: "主因",
    summary_contains: "摘要",
    action_keys_contains: "下一步建议",
    factor_keys_contains: "关键因子"
  };
  return labels[field] || field;
}
function formatPresenceReason(value: unknown) {
  const raw = String(value || "").trim();
  if (!raw) return "-";
  const cue = presence.value?.social_cues?.find((item: any) => item.key === raw);
  if (cue?.label) return cue.label;
  if (raw.startsWith("interest=")) return "话题兴趣";
  if (raw.startsWith("recent_reply=")) return "刚发过言";
  return raw;
}
function cueClass(impact: unknown) {
  if (impact === "boost" || impact === "faster") return "ok";
  if (impact === "penalty" || impact === "slower") return "degraded";
  if (impact === "block") return "down";
  return "pending";
}
function formatQuoteReason(value: unknown) {
  const key = String(value || "");
  const labels: Record<string, string> = {
    at_mention: "@Bot 引用概率",
    ambient_group: "普通群聊引用概率",
    missing_message_id: "缺少消息 ID",
    private_chat: "私聊不引用"
  };
  return labels[key] || key || "引用策略未知";
}
function formatStickerPlacement(value: unknown) {
  const key = String(value || "");
  const labels: Record<string, string> = {
    append: "跟随发送",
    replace_text: "替代文字"
  };
  return labels[key] || key || "-";
}
function formatUnitTypes(value: unknown) {
  if (!Array.isArray(value) || !value.length) return "-";
  const labels: Record<string, string> = {
    text: "文字",
    sticker: "表情",
    voice: "语音",
    correction: "修正",
    recall: "撤回",
    image: "图片"
  };
  return value.map((item) => labels[String(item)] || String(item)).join(" / ");
}
function formatTimingUnitTypes(value: unknown) {
  if (!Array.isArray(value) || !value.length) return "-";
  return value.map((item: any) => formatUnitTypes([item?.unit_type || item])).join(" / ");
}
function sourceLabel(source: any) {
  if (!source) return "未知";
  return [source.type, source.ref].filter(Boolean).join(" / ") || source.preview || "未知";
}
function recallVerdict(row: any) {
  if (row.why_recalled?.lexical_match) return "原文命中";
  const semantic = Number(row.why_recalled?.semantic_score ?? row.semantic_score ?? 0);
  const keyword = Number(row.why_recalled?.keyword_score ?? row.keyword_score ?? 0);
  if (semantic >= keyword) return "语义召回";
  return "关键词召回";
}
function recallDetail(row: any) {
  const sources = row.why_recalled?.retrieval_sources || [];
  const status = row.status || "active";
  const importance = row.importance ?? row.metadata?.importance ?? "-";
  return `状态 ${status}，重要度 ${importance}，召回源 ${sources.length ? sources.join(", ") : "-"}`;
}
async function loadInitialData() {
  error.value = "";
  try {
    const [presetResponse, personaResponse] = await Promise.all([
      api<{ presets: SimulatePreset[]; expectation_schema?: SimulateExpectationSchema }>("/api/simulate/presets"),
      api<{ personas: string[] }>("/api/personas")
    ]);
    presets.value = presetResponse.presets || [];
    expectationSchema.value = presetResponse.expectation_schema || {};
    personas.value = personaResponse.personas || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : "调试初始数据加载失败";
  }
}
function syncSelectedPersona() {
  if (!form.persona) {
    selectedPersona.value = "";
  } else if (personas.value.includes(form.persona)) {
    selectedPersona.value = form.persona;
  } else {
    selectedPersona.value = "__custom";
  }
}
function applySelectedPersona() {
  if (selectedPersona.value === "__custom") return;
  form.persona = selectedPersona.value;
}
function applyPreset(preset: SimulatePreset) {
  const payload = preset.payload || {};
  selectedPreset.value = preset.id;
  selectedExpectations.value = preset.expectations || {};
  form.message = String(payload.message || "");
  form.user_id = String(payload.user_id || "10001");
  form.group_id = payload.group_id ? String(payload.group_id) : "";
  form.persona = String(payload.persona || "");
  syncSelectedPersona();
  form.nickname = String(payload.nickname || "Simulator");
  form.self_id = String(payload.self_id || "99999");
  form.at_bot = Boolean(payload.at_bot);
  form.notice_poke = Boolean(payload.notice_poke || payload.notice_origin?.kind === "poke");
  form.affinity_level = String(payload.affinity_level || "");
  imageUrlsText.value = Array.isArray(payload.image_urls) ? payload.image_urls.join("\n") : String(payload.image_urls || "");
  recordUrl.value = String(payload.record_url || "");
  historyJson.value = JSON.stringify(payload.history || [], null, 2);
  interactionHistoryJson.value = JSON.stringify(payload.interaction_history || [], null, 2);
}
function resetInput() {
  selectedPreset.value = "";
  selectedExpectations.value = {};
  Object.assign(form, {
    message: "",
    user_id: "10001",
    group_id: "",
    persona: "",
    nickname: "Simulator",
    self_id: "99999",
    at_bot: false,
    notice_poke: false,
    affinity_level: ""
  });
  historyJson.value = "[]";
  interactionHistoryJson.value = "[]";
  imageUrlsText.value = "";
  recordUrl.value = "";
  syncSelectedPersona();
}
async function copyResult() {
  if (!result.value) return;
  await copyText(result.value, "结果已复制");
}
async function simulate() {
  error.value = "";
  actionResult.value = "";
  payload.value = null;
  let history: unknown[] = [];
  let interactionHistory: unknown[] = [];
  try {
    const parsed = JSON.parse(historyJson.value || "[]");
    if (!Array.isArray(parsed)) throw new Error("历史 JSON 必须是数组");
    history = parsed;
  } catch (err) {
    error.value = err instanceof Error ? err.message : "历史 JSON 格式错误";
    return;
  }
  try {
    const parsed = JSON.parse(interactionHistoryJson.value || "[]");
    if (!Array.isArray(parsed)) throw new Error("群互动 JSON 必须是数组");
    interactionHistory = parsed;
  } catch (err) {
    error.value = err instanceof Error ? err.message : "群互动 JSON 格式错误";
    return;
  }
  running.value = true;
  try {
    const response = await postJson<any>("/api/simulate", {
      ...form,
      group_id: form.group_id || null,
      persona: form.persona || null,
      at_bot: Boolean(form.group_id && form.at_bot),
      notice_origin: form.notice_poke ? { kind: "poke" } : {},
      image_urls: imageUrls.value,
      record_url: recordUrl.value.trim() || null,
      affinity_level: form.affinity_level || null,
      expectations: selectedExpectations.value,
      interaction_history: interactionHistory,
      history
    });
    payload.value = response;
    result.value = JSON.stringify(response, null, 2);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "模拟失败";
  } finally {
    running.value = false;
  }
}

onMounted(async () => {
  await loadInitialData();
  syncSelectedPersona();
});
</script>

<style scoped>
h3 { font-size: 15px; margin: 16px 0 8px; }
.preset-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}
.preset-button {
  min-height: 92px;
  display: grid;
  align-content: start;
  gap: 6px;
  text-align: left;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  color: var(--text);
  padding: 10px;
  cursor: pointer;
}
.preset-button.active {
  border-color: var(--primary);
  background: #16384a;
}
.preset-button strong {
  font-size: 14px;
}
.preset-button span {
  color: var(--muted);
  font-size: 12px;
  line-height: 1.45;
}
.expectation-catalog {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin: 10px 0;
}
.expectation-catalog strong {
  color: var(--muted);
  font-size: 13px;
  margin-right: 2px;
}
.expectation-catalog span {
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--panel-2);
  color: var(--text);
  font-size: 12px;
  padding: 4px 8px;
}
.detail-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  color: var(--muted);
  font-size: 13px;
}
.detail-strip span {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 4px 8px;
  background: var(--panel-2);
}
.social-summary {
  margin-top: 8px;
  color: var(--text);
  font-size: 13px;
}
.social-cues {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.table-subtext {
  display: block;
  color: var(--muted);
  font-size: 12px;
  margin-top: 4px;
}
.run-summary {
  display: grid;
  gap: 6px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px 12px;
  margin: 10px 0;
}
.run-summary strong {
  font-size: 16px;
}
.run-summary span {
  color: var(--muted);
  overflow-wrap: anywhere;
}
.diagnostic-advice {
  display: grid;
  gap: 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px 12px;
  margin: 10px 0;
}
.diagnostic-advice.ok {
  border-color: #386f47;
}
.diagnostic-advice.degraded {
  border-color: #80652e;
}
.diagnostic-advice.down {
  border-color: #873b3b;
}
.diagnostic-head {
  display: grid;
  gap: 4px;
}
.diagnostic-head strong {
  font-size: 14px;
}
.diagnostic-head span {
  color: var(--text);
  font-size: 13px;
  line-height: 1.45;
}
.diagnostic-factors {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.diagnostic-factors span {
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--panel);
  color: var(--text);
  font-size: 12px;
  padding: 5px 8px;
}
.diagnostic-factors span.ok {
  border-color: #386f47;
}
.diagnostic-factors span.degraded {
  border-color: #80652e;
}
.diagnostic-factors span.down {
  border-color: #873b3b;
}
.diagnostic-factors strong {
  margin-right: 5px;
  color: var(--muted);
}
.diagnostic-factors small {
  margin-left: 5px;
  color: var(--muted);
}
.diagnostic-actions {
  display: grid;
  gap: 6px;
}
.diagnostic-actions div {
  display: grid;
  gap: 3px;
  border-left: 2px solid var(--border);
  padding-left: 8px;
}
.diagnostic-actions strong {
  font-size: 13px;
}
.diagnostic-actions span {
  color: var(--muted);
  font-size: 12px;
  line-height: 1.45;
}
.save-result {
  border: 1px solid #386f47;
  background: #142016;
  color: var(--success);
  border-radius: 8px;
  padding: 10px 12px;
}
.expectation-panel {
  margin-top: 14px;
}
.expectation-row {
  display: grid;
  grid-template-columns: 52px minmax(120px, 1fr) minmax(90px, auto);
  gap: 8px;
  align-items: center;
  border: 1px solid var(--border);
  border-radius: 7px;
  padding: 7px 9px;
  margin-bottom: 8px;
  background: var(--panel-2);
}
.expectation-row.passed strong {
  color: var(--success);
}
.expectation-row.failed strong {
  color: var(--danger);
}
.expectation-row span {
  color: var(--text);
  font-size: 13px;
}
.expectation-row code {
  color: var(--muted);
  font-size: 12px;
  text-align: right;
}
.side-effects {
  margin-top: 14px;
}
.effect-group {
  display: grid;
  grid-template-columns: 72px 1fr;
  gap: 8px;
  align-items: start;
  margin: 8px 0;
}
.effect-group strong {
  color: var(--muted);
  font-size: 13px;
  line-height: 28px;
}
.effect-group span {
  display: inline-flex;
  width: fit-content;
  margin: 0 6px 6px 0;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 4px 8px;
  background: var(--panel-2);
  color: var(--text);
  font-size: 13px;
}
</style>
